import {Injectable} from '@angular/core';
import { Router} from '@angular/router';
import {Actions, createEffect,  ofType} from '@ngrx/effects';
import { select, Store} from '@ngrx/store';
import * as _ from 'lodash';
import {catchError, delayWhen, filter, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {BaseEffect} from './base.effect';
import * as fromShared from '../../shared/reducers';
import {error} from '../../shared/actions/toasts';
import {
  ChangeInvoicesPageSizeAction,
  invoicesListActionTypes, LoadInvoicesAction, LoadInvoicesFailAction, LoadInvoicesSuccessAction, OrderInvoicesByAction,
  PageInvoicesToAction,
  QuickSearchInvoicesAction,
  SearchInvoicesAction
} from '../actions/invoices-list';
import {ArrayResponse, cleanObject, DEFAULT_PAGE_SIZE, go, SendBackResult} from '..';
import {debug} from '../../../rxjs-operators';
import {InvoicesService} from '../services/invoices.service';
import {L10nTranslationService} from 'angular-l10n';

@Injectable({providedIn: 'root'})
export class InvoicesListEffect extends BaseEffect {
  private routerState$ = this.store.pipe(select(fromShared.selectors.getRouterState) , filter(state => !_.isEmpty(state)));


  pageTo$ = createEffect(() => { return this.actions$.pipe(ofType<PageInvoicesToAction>(invoicesListActionTypes.PAGE_TO),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { $page: action.payload })),
    map(query => go([], query)))
  });


  changePageSize$ = createEffect(() => { return this.actions$.pipe(ofType<ChangeInvoicesPageSizeAction>(invoicesListActionTypes.CHANGE_PAGE_SIZE),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { $length: action.payload })),
    map(query => go([], query)))
  });


  orderBy$ = createEffect(() => { return this.actions$.pipe(ofType<OrderInvoicesByAction>(invoicesListActionTypes.ORDER_BY),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { $orderBy: action.payload })),
    map(query => go([], query)))
  });


  quickSearch$ = createEffect(() => { return this.actions$.pipe(ofType<QuickSearchInvoicesAction>(invoicesListActionTypes.QUICK_SEARCH),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, query, { quickSearch: action.payload, $page: 1 })),
    map(query => cleanObject(query, ['$page', '$length', '$orderBy', 'quickSearch'], true)),
    map(query => go([], query)))
  });


  search$ = createEffect(() => { return this.actions$.pipe(ofType<SearchInvoicesAction>(invoicesListActionTypes.SEARCH),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map(([action, query]) => Object.assign({}, cleanObject(query, ['$page', '$length', '$orderBy'], true), action.payload, { $page: 1 })),
    map(query => go([], query)))
  });


  load$ = createEffect(() => { return this.actions$.pipe(ofType(invoicesListActionTypes.LOAD),
    delayWhen(() => this.routerState$),
    debug('Load contracts list action received.'),
    map((action: LoadInvoicesAction) => action.payload),
    withLatestFrom(this.routerState$.pipe(map(state => state.queryParams))),
    map((query) => Object.assign({}, { $page: 1, $length: DEFAULT_PAGE_SIZE }, query)),
    switchMap(([ payload, filterD]) => this.invoicesService.getList(payload.route, payload.uuid, filterD).pipe(
      // tslint:disable-next-line:no-any
      map((payloadR: SendBackResult<ArrayResponse<any>>) => new LoadInvoicesSuccessAction(payloadR.data)),
      catchError(res => this.catchResponseError(res)))))
  });

  loadFail$ = createEffect(() => { return this.actions$.pipe(ofType(invoicesListActionTypes.LOAD_FAIL),
    debug('A server error occurred while retrieving contracts list.'),
    map(() => error(this.translation.translate('INVOICES_LOAD_ERROR'), this.translation.translate('TOAST_ERROR_TITLE'))))
  });

  constructor(private actions$: Actions,
              private store: Store<fromShared.AppState>,
              private translation: L10nTranslationService,
              private invoicesService: InvoicesService,
              router: Router) {
    super(router);
  }
  protected handleUnhandledError = (response) => new LoadInvoicesFailAction(response['error'].message)

  protected handleGoneError = (response) => {
    return new LoadInvoicesFailAction(response['error'].message);
  }
}
